import React, { useCallback, useEffect, useState } from "react"

import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import OutlinedInput from "@mui/material/OutlinedInput"
import Chip from "@mui/material/Chip"
import Skeleton from "@mui/material/Skeleton"
import Alert from "@mui/material/Alert"
import CircularProgress from "@mui/material/CircularProgress"
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material"
import SnoozeIcon from "@mui/icons-material/Snooze"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import { ChevronLeft, ChevronRight } from "@mui/icons-material"
import dayjs from "dayjs"
import "dayjs/locale/fr"


// ---------------------------------------------------------------------------
// Error code → human-readable French message
// ---------------------------------------------------------------------------
const friendlyError = (error) => {
    switch (error?.code) {
        case "23503":
            return "Impossible de modifier : une contrainte de clé étrangère bloque cette opération (une transaction existante référence ce bail)."
        case "23505":
            return "Impossible d'enregistrer : une valeur en double existe déjà."
        case "42501":
            return "Droits insuffisants pour effectuer cette opération. Contactez votre administrateur."
        case "PGRST301":
            return "Session expirée. Veuillez vous reconnecter."
        default:
            return error?.message
                ? `Erreur inattendue : ${error.message}`
                : "Une erreur inconnue est survenue. Veuillez réessayer."
    }
}


// ---------------------------------------------------------------------------
// Loading skeleton — mirrors the card's layout to prevent layout shift
// ---------------------------------------------------------------------------
const IndexCardSkeleton = () => (
    <Card variant="outlined" sx={{ padding: 0 }}>
        <Box sx={{
            display: "flex",
            alignItems: "center",
            p: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
        }}>
            <Skeleton variant="text" width={160} height={24} />
            <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
            </Box>
        </Box>
        <Stack spacing={2} sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Skeleton variant="text" width={120} height={22} />
                <Skeleton variant="rounded" width={60} height={22} />
            </Box>
            <Skeleton variant="text" width={200} height={18} />
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
        </Stack>
    </Card>
)


// ---------------------------------------------------------------------------
// Main component
//
// Props
//   supabase    – initialised Supabase client
//   propertyId  – (optional) UUID of a specific property.
//                 When provided (property page): filters results to this property
//                 and shows an explicit "up to date" state when nothing is pending.
//                 When omitted (dashboard): behaves as before — shows all overdue
//                 indexations across the portfolio and returns null when the list
//                 is empty so the dashboard card disappears.
// ---------------------------------------------------------------------------
const IndexCard = ({ supabase, propertyId = null }) => {

    const [indexations,  setIndexations]  = useState([])
    const [navIndex,     setNavIndex]     = useState(0)
    const [bucketValues, setBucketValues] = useState({})

    const [loading,     setLoading]     = useState(true)
    const [fetchError,  setFetchError]  = useState(null)
    const [submitting,  setSubmitting]  = useState(false)
    const [submitError, setSubmitError] = useState(null)


    // -----------------------------------------------------------------------
    // Data fetching
    // -----------------------------------------------------------------------
    const fetchIndexations = useCallback(async () => {
        setLoading(true)
        setFetchError(null)

        let query = supabase.from("v_overdue_indexation").select("*")
        if (propertyId) query = query.eq("property_id", propertyId)

        const { data, error } = await query

        if (error) {
            setFetchError(friendlyError(error))
            setLoading(false)
            return
        }

        setIndexations(data ?? [])
        setNavIndex(0)
        setLoading(false)
    }, [supabase, propertyId])

    useEffect(() => {
        fetchIndexations()
    }, [fetchIndexations])


    // -----------------------------------------------------------------------
    // Initialise editable bucket values whenever the visible item changes
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (!indexations.length) return
        const current = indexations[navIndex]
        if (!current) return

        const initial = {}
        current.buckets.forEach((b) => {
            initial[b.transactions_bucket_id_old] = b.default_value_new
        })
        setBucketValues(initial)
        setSubmitError(null)
    }, [indexations, navIndex])


    // -----------------------------------------------------------------------
    // Submit — calls the atomic apply_indexation RPC
    // -----------------------------------------------------------------------
    const handleSubmit = async () => {
        setSubmitting(true)
        setSubmitError(null)

        const current      = indexations[navIndex]
        const priceIndexId = current.buckets[0]?.price_index_id_new

        const bucketsPayload = current.buckets.map((b) => ({
            bucket_id: b.transactions_bucket_id_old,
            category:  b.category,
            value: parseFloat(
                bucketValues[b.transactions_bucket_id_old] ?? b.default_value_new
            ),
        }))

        const { error } = await supabase.rpc("apply_indexation", {
            p_lease_id:       current.lease_id,
            p_price_index_id: priceIndexId,
            p_buckets:        bucketsPayload,
        })

        if (error) {
            setSubmitError(friendlyError(error))
            setSubmitting(false)
            return
        }

        await fetchIndexations()
        setSubmitting(false)
    }


    // -----------------------------------------------------------------------
    // Render: loading
    // -----------------------------------------------------------------------
    if (loading) return <IndexCardSkeleton />


    // -----------------------------------------------------------------------
    // Render: fetch error
    // -----------------------------------------------------------------------
    if (fetchError) {
        return (
            <Card variant="outlined" sx={{ padding: 2 }}>
                <Alert
                    severity="error"
                    action={
                        <Button size="small" onClick={fetchIndexations}>
                            Réessayer
                        </Button>
                    }
                >
                    {fetchError}
                </Alert>
            </Card>
        )
    }


    // -----------------------------------------------------------------------
    // Render: nothing overdue
    //
    // DECISION: behaviour differs by context.
    //   Dashboard (no propertyId): return null so the card disappears from the
    //     suggestions area — consistent with the existing pattern.
    //   Property page (propertyId set): show an explicit "up to date" indicator
    //     so the actions section always gives clear feedback, even when there is
    //     nothing to do.
    // -----------------------------------------------------------------------
    if (!indexations.length) {
        return (
            <Card variant="outlined" sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <CheckCircleOutlineIcon color="success" sx={{ flexShrink: 0 }} />
                <Box>
                    <Typography variant="body2" fontWeight={600}>Ré-indexation à jour</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {propertyId
                            ? "Aucune ré-indexation en attente pour ce bien."
                            : "Aucune ré-indexation en attente."
                        }
                    </Typography>
                </Box>
            </Card>
        )
    }


    // -----------------------------------------------------------------------
    // Render: card
    // -----------------------------------------------------------------------
    const current    = indexations[navIndex]
    const totalOld   = current.buckets.reduce((s, b) => s + b.default_value_old, 0)
    const totalNew   = current.buckets.reduce((s, b) => s + b.default_value_new, 0)
    const pctChange  = totalOld > 0 ? 100 * (totalNew / totalOld - 1) : 0
    const nextIndexedOn = dayjs(current.last_indexed_on).add(1, "year")

    // Navigation is only meaningful when there are multiple items to browse.
    const canNavigate = indexations.length > 1

    return (
        <Card sx={{ padding: 0 }} variant="outlined">

            {/* ── Header ────────────────────────────────────────────────── */}
            <Box sx={{
                display: "flex",
                flexDirection: "row",
                backgroundColor: "background.paper",
                p: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
            }}>
                <Typography component="p" sx={{
                    fontSize: 16,
                    fontWeight: 600,
                    marginRight: "auto",
                    marginY: "auto",
                    paddingLeft: 1,
                }}>
                    Ré-indexation due
                    {canNavigate && (
                        <Typography
                            component="span"
                            sx={{ ml: 1, fontSize: 13, fontWeight: 400, color: "text.secondary" }}
                        >
                            ({navIndex + 1} / {indexations.length})
                        </Typography>
                    )}
                </Typography>

                {/* Snooze + navigation — hidden when scoped to a single property,
                    where there is at most one pending item and no browsing needed. */}
                {!propertyId && (
                    <>
                        <IconButton sx={{ marginRight: 1 }} size="small" disabled={submitting}>
                            <SnoozeIcon />
                        </IconButton>
                        <IconButton
                            sx={{ marginRight: 1 }}
                            size="small"
                            onClick={() => setNavIndex((navIndex - 1 + indexations.length) % indexations.length)}
                            disabled={!canNavigate || navIndex === 0 || submitting}
                        >
                            <ChevronLeft />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => setNavIndex((navIndex + 1) % indexations.length)}
                            disabled={!canNavigate || navIndex === indexations.length - 1 || submitting}
                        >
                            <ChevronRight />
                        </IconButton>
                    </>
                )}
            </Box>

            {/* ── Body ──────────────────────────────────────────────────── */}
            <Stack direction="column" spacing={0} sx={{ p: 2 }}>

                {/* Property name + percentage badge
                    DECISION: hide property name when scoped to a single property —
                    the user already knows which property they're on. */}
                {!propertyId && (
                    <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography component="p" sx={{ fontSize: 16, fontWeight: 600 }}>
                                {current.property_short_name}
                            </Typography>
                            <Chip
                                label={`+${pctChange.toLocaleString("fr-FR", {
                                    minimumFractionDigits: 1,
                                    maximumFractionDigits: 1,
                                })}%`}
                                color="success"
                                size="small"
                            />
                        </Box>
                        <Typography component="p" variant="body2" color="text.secondary">
                            Due le {nextIndexedOn.locale("fr").format("DD MMM")}
                        </Typography>
                    </Box>
                )}

                {/* When scoped to a property, show the percentage badge inline */}
                {propertyId && (
                    <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography component="p" variant="body2" color="text.secondary">
                            Due le {nextIndexedOn.locale("fr").format("DD MMM YYYY")}
                        </Typography>
                        <Chip
                            label={`+${pctChange.toLocaleString("fr-FR", {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1,
                            })}%`}
                            color="success"
                            size="small"
                        />
                    </Box>
                )}

                {/* Submission error */}
                {submitError && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        onClose={() => setSubmitError(null)}
                    >
                        {submitError}
                    </Alert>
                )}

                {/* Index + bucket table */}
                <TableContainer sx={{ mb: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 150, whiteSpace: "nowrap" }} />
                                <TableCell sx={{ minWidth: 100, whiteSpace: "nowrap" }}>
                                    <b>Actuellement</b>
                                </TableCell>
                                <TableCell sx={{ minWidth: 100, whiteSpace: "nowrap" }}>
                                    <b>Mise à jour</b>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>

                            {/* Index period row */}
                            <TableRow>
                                <TableCell><b>{current.index_name} en date du</b></TableCell>
                                <TableCell>{current.index_period_old}</TableCell>
                                <TableCell>{current.index_period_new}</TableCell>
                            </TableRow>

                            {/* Index value row */}
                            <TableRow>
                                <TableCell><b>Valeur de l'indice</b></TableCell>
                                <TableCell>
                                    {current.index_value_old?.toLocaleString("fr-FR", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </TableCell>
                                <TableCell>
                                    {current.index_value_new?.toLocaleString("fr-FR", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </TableCell>
                            </TableRow>

                            {/* One row per bucket (sorted alphabetically by category) */}
                            {current.buckets
                                .slice()
                                .sort((a, b) => a.category.localeCompare(b.category))
                                .map((bucket) => (
                                    <TableRow key={bucket.transactions_bucket_id_old}>
                                        <TableCell><b>{bucket.category}</b></TableCell>
                                        <TableCell>
                                            {bucket.default_value_old.toLocaleString("fr-FR", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })} €
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 100 }}>
                                            <OutlinedInput
                                                placeholder="123"
                                                endAdornment="€"
                                                value={
                                                    bucketValues[bucket.transactions_bucket_id_old]
                                                    ?? bucket.default_value_new
                                                }
                                                onChange={(e) =>
                                                    setBucketValues((prev) => ({
                                                        ...prev,
                                                        [bucket.transactions_bucket_id_old]: e.target.value,
                                                    }))
                                                }
                                                size="small"
                                                type="number"
                                                inputProps={{ min: 0, step: 0.01 }}
                                                disabled={submitting}
                                                sx={{
                                                    minWidth: 100,
                                                    width: "100%",
                                                    maxHeight: 30,
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Button
                    variant="contained"
                    sx={{ width: "100%" }}
                    onClick={handleSubmit}
                    disabled={submitting}
                    startIcon={
                        submitting
                            ? <CircularProgress size={16} color="inherit" />
                            : null
                    }
                >
                    {submitting ? "Enregistrement…" : "Enregistrer"}
                </Button>
            </Stack>
        </Card>
    )
}

export default IndexCard